import { useState, useCallback, useEffect } from "react";

/**
 * useTabState 是一個自訂 Hook，用於管理分頁（Tab）元件的目前選取狀態。
 * 接收初始值，並在使用者切換分頁時更新選中的 tab。
 *
 * @param {string|number} initialTab - 初始選取的分頁鍵值（可為字串或數字）
 *
 * @returns {Object} 一個包含以下屬性的物件：
 * - activeTab：目前選取的分頁鍵值
 * - handleTabChange(event: Event, newValue: string|number)：切換分頁時呼叫的處理函式
 *
 * @example
 * const { activeTab, handleTabChange } = useTabState(0);
 *
 * <Tabs value={activeTab} onChange={handleTabChange}>
 *   <Tab label="Tab1" value={0} />
 *   <Tab label="Tab2" value={1} />
 * </Tabs>
 */
const useTabState = initialTab => {
	const [activeTab, setActiveTab] = useState(initialTab);

	/**
	 * 處理分頁變更事件，更新目前選取的分頁狀態
	 *
	 * @param {React.SyntheticEvent} event - 事件物件（通常來自 Tabs onChange）
	 * @param {string|number} newValue - 新的分頁鍵值
	 */
	const handleTabChange = useCallback(
		(event, newValue) => {
			setActiveTab(newValue);
		},
		// eslint-disable-next-line
		[]
	);

	useEffect(
		() => {
			setActiveTab(initialTab);
		},
		// eslint-disable-next-line
		[initialTab]
	);

	return { activeTab, handleTabChange };
};

export default useTabState;
